
import React, { createContext, useContext, useEffect, useCallback, useMemo, useReducer, useRef } from 'react';
import services from '../services';
import { storyService } from '../services/storyService';
import { useAuth } from './AuthContext';
import { authState } from '../services/authState';

// Create context
export const StoryContext = createContext();

// Action types for reducer
const StoryActionTypes = {
  SET_STORY: 'SET_STORY',
  UPDATE_PAGE: 'UPDATE_PAGE',
  SET_LOADING: 'SET_LOADING',
  SET_CREATING: 'SET_CREATING',
  SET_ERROR: 'SET_ERROR',
  RESET_STORY: 'RESET_STORY',
  // New action types for story collection management
  SET_STORIES: 'SET_STORIES',
  UPDATE_STORY_IN_LIST: 'UPDATE_STORY_IN_LIST',
  SET_STORIES_LOADING: 'SET_STORIES_LOADING',
  SET_STORIES_ERROR: 'SET_STORIES_ERROR',
  // Action types for read-aloud story
  SET_READ_ALOUD_STORY: 'SET_READ_ALOUD_STORY',
  SET_READ_ALOUD_STORY_LOADING: 'SET_READ_ALOUD_STORY_LOADING',
  SET_READ_ALOUD_STORY_ERROR: 'SET_READ_ALOUD_STORY_ERROR'
};

// Initial state
const initialState = {
  story: null,
  isLoading: false,
  isCreatingStory: false,
  error: null,
  // New state for story collection management
  stories: [],
  storiesLoading: false,
  storiesError: null,
  // State for read-aloud story (separate from active story)
  readAloudStory: null,
  readAloudStoryLoading: false,
  readAloudStoryError: null
};

// Reducer function
function storyReducer(state, action) {
  switch (action.type) {
    case StoryActionTypes.SET_STORY:
      // Add detailed debugging about the action payload, including source
      console.log('[StoryReducer] Detailed action payload:', {
        source: new Error().stack.split('\n')[2].trim(),  // This will show where the action is coming from
        payloadId: action.payload?.id,
        payloadKeys: Object.keys(action.payload || {}),
        payloadHasUserIsOwner: action.payload?.user_is_owner !== undefined,
        payloadValueUserIsOwner: action.payload?.user_is_owner,
        currentHasUserIsOwner: state.story?.user_is_owner !== undefined,
        currentUserIsOwner: state.story?.user_is_owner,
        payloadHasPages: !!action.payload?.pages,
        payloadPagesCount: action.payload?.pages?.length || 0
      });
      
      // Check if this is a title update (has title but no pages)
      const isTitleUpdate = action.payload.title && 
                           (!action.payload.pages || action.payload.pages.length === 0) && 
                           state.story?.pages?.length > 0;
      
      if (isTitleUpdate) {
        console.log('[StoryReducer] This is a title-only update');
        // Only update the title while preserving other state including ownership
        return {
          ...state,
          story: {
            ...state.story,
            title: action.payload.title
          }
        };
      }
      
      // Get pages from the payload or existing story
      let updatedPages = action.payload.pages || state.story?.pages || [];
      
      // Handle ownership preservation
      let finalUserIsOwner;
      
      // Track which fields came from where
      const fieldsFromPayload = [];
      const fieldsFromState = [];
      
      // Case 1: New payload explicitly sets user_is_owner (highest priority)
      if (action.payload.user_is_owner !== undefined) {
        finalUserIsOwner = action.payload.user_is_owner;
        console.log(`[StoryReducer] Using ownership from payload: ${finalUserIsOwner}`);
        fieldsFromPayload.push('user_is_owner');
      }
      // Case 2: Current state has user_is_owner defined, preserve it
      else if (state.story?.user_is_owner !== undefined) {
        finalUserIsOwner = state.story.user_is_owner;
        console.log(`[StoryReducer] Preserving ownership from state: ${finalUserIsOwner}`);
        fieldsFromState.push('user_is_owner');
      }
      // Case 3: Neither has it defined, default to false for security
      else {
        finalUserIsOwner = false;
        console.log('[StoryReducer] No ownership information available, defaulting to false');
      }
      
      // Create a deep-copied preserved state to avoid reference issues
      const preservedState = {
        user_is_owner: finalUserIsOwner
      };
      
      // Always preserve story ID if it exists in current state but is missing in payload
      if (state.story?.id && !action.payload.id) {
        preservedState.id = state.story.id;
        fieldsFromState.push('id');
        console.log(`[StoryReducer] Preserving id from state: "${state.story.id}"`);
      }
      
      // Always preserve important fields if they exist in current state but are missing in payload
      if (state.story?.theme && !action.payload.theme) {
        preservedState.theme = state.story.theme;
        fieldsFromState.push('theme');
        console.log(`[StoryReducer] Preserving theme from state: "${state.story.theme}"`);
      }
      
      if (state.story?.desired_ending && !action.payload.desired_ending) {
        preservedState.desired_ending = state.story.desired_ending;
        fieldsFromState.push('desired_ending');
        console.log(`[StoryReducer] Preserving desired_ending from state: "${state.story.desired_ending}"`);
      }
      
      if (state.story?.title && !action.payload.title) {
        preservedState.title = state.story.title;
        fieldsFromState.push('title');
        console.log(`[StoryReducer] Preserving title from state: "${state.story.title}"`);
      }
      
      // Log clearly which fields came from where
      if (fieldsFromState.length > 0) {
        console.log(`[StoryReducer] Fields preserved from state: ${fieldsFromState.join(', ')}`);
      }
      if (fieldsFromPayload.length > 0) {
        console.log(`[StoryReducer] Fields obtained from payload: ${fieldsFromPayload.join(', ')}`);
      }
      
      // Perform full update with careful state preservation
      return {
        ...state,
        story: {
          ...action.payload,
          pages: updatedPages,
          ...preservedState
        }
      };
      
    case StoryActionTypes.UPDATE_PAGE: {
      if (!state.story) {
        return state;
      }
      
      // Check if the page exists in the current pages array
      const pageExists = state.story.pages && 
                         state.story.pages.some(page => page.id === action.payload.id);
      
      let newPages;
      if (pageExists && state.story.pages) {
        // Update existing page, preserving any fields not in the update
        const existingPage = state.story.pages.find(p => p.id === action.payload.id);
        const updatedPage = {
          ...existingPage,
          ...action.payload,
          // Keep existing text if update doesn't include it
          text: action.payload.text || existingPage.text
        };
        
        newPages = state.story.pages.map(page =>
          page.id === action.payload.id ? updatedPage : page
        );
      } else {
        // Add new page - no sorting needed as backend provides sorted data
        newPages = [...(state.story.pages || []), action.payload];
      }
      
      // CRITICAL FIX: If this is the first page of the story, check for missing text
      // Sometimes the first page.text is missing, but the text does exist in subsequent updates
      if (newPages[0]?.id === action.payload.id && !newPages[0].text && action.payload.text) {
        // Make a deep copy of the first page and update its text
        newPages[0] = {
          ...newPages[0],
          text: action.payload.text
        };
      }
      
      // No explicit logging needed
      
      return {
        ...state,
        story: {
          ...state.story,
          pages: newPages,
          total_pages: Math.max(state.story.total_pages || 0, newPages.length),
          // Always preserve user_is_owner field from current state
          user_is_owner: state.story.user_is_owner
        }
      };
    }

    case StoryActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    case StoryActionTypes.SET_CREATING:
      return {
        ...state,
        isCreatingStory: action.payload
      };
      
    case StoryActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };
      
    case StoryActionTypes.RESET_STORY:
      return {
        ...state,
        story: null,
        isCreatingStory: false,
        error: null
      };
    
    // New cases for story collection management
    case StoryActionTypes.SET_STORIES:
      return {
        ...state,
        stories: action.payload,
        storiesLoading: false,
        storiesError: null
      };
      
    case StoryActionTypes.UPDATE_STORY_IN_LIST:
      // Only update if the story exists in the collection
      if (!state.stories.some(s => s.id === action.payload.id)) {
        return state;
      }
      
      return {
        ...state,
        stories: state.stories.map(s => 
          s.id === action.payload.id ? action.payload : s
        )
      };
      
    case StoryActionTypes.SET_STORIES_LOADING:
      return {
        ...state,
        storiesLoading: action.payload
      };
      
    case StoryActionTypes.SET_STORIES_ERROR:
      return {
        ...state,
        storiesError: action.payload,
        storiesLoading: false
      };
      // Handle read-aloud story actions
    case StoryActionTypes.SET_READ_ALOUD_STORY:
      return {
        ...state,
        readAloudStory: action.payload,
        readAloudStoryLoading: false,
        readAloudStoryError: null
      };
      
    case StoryActionTypes.SET_READ_ALOUD_STORY_LOADING:
      return {
        ...state,
        readAloudStoryLoading: action.payload
      };
      
    case StoryActionTypes.SET_READ_ALOUD_STORY_ERROR:
      return {
        ...state,
        readAloudStoryError: action.payload,
        readAloudStoryLoading: false
      };
    
    default:
      return state;
  }
}

export function StoryProvider({ children }) {
  const [state, dispatch] = useReducer(storyReducer, initialState);
  const { 
    story, isLoading, isCreatingStory, error,
    // Extract new state for story collection
    stories, storiesLoading, storiesError,
    // Extract read-aloud story state
    readAloudStory, readAloudStoryLoading, readAloudStoryError 
  } = state;
  const { isAuthenticated, user, activeChildProfile } = useAuth();

  // Store connection state in a ref to maintain it across renders
  const wsConnectionRef = useRef({
    isConnected: false,
    isConnecting: false,
    hasAttemptedConnection: false,
    messageHandler: null,
    errorHandler: null,
    messageUnsubscribe: null,
    errorUnsubscribe: null
  });
  
  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((event) => {
    if (!event || (typeof event !== 'object')) {
      console.warn('Received invalid WebSocket message');
      return;
    }
  
    const { type, data } = event;
    
    // Detailed debug log for all WebSocket messages
    console.log('[StoryContext] WebSocket message received:', {
      type,
      hasData: !!data,
      dataId: data?.id,
      hasPages: !!data?.pages,
      hasUserIsOwner: data?.user_is_owner !== undefined,
      userIsOwner: data?.user_is_owner,
      dataKeys: data ? Object.keys(data) : [],
      depth1Keys: data ? Object.keys(data).filter(k => typeof data[k] !== 'object' || data[k] === null) : [],
      depth2Keys: data ? Object.keys(data).filter(k => typeof data[k] === 'object' && data[k] !== null) : [],
      currentStoryInState: state.story?.id
    });
    
    // With our standardized format, we no longer need to handle special cases
    // like top-level user_is_owner. All data is consistently structured.
  
    switch (type) {
      case 'page_update':
        if (data) {
          // NEW ARCHITECTURE: Use direct object format without 'data' wrapper
          // StoryContext now standardizes on direct object format across the application
          
          // Log the format standardization for debugging with comprehensive details
          console.log('[StoryContext] Standardizing to direct object format for page_update:', {
            originalHasId: !!data.id,
            originalHasUserIsOwner: data.user_is_owner !== undefined,
            originalUserIsOwner: data.user_is_owner,
            dataAllFields: Object.keys(data),
            dataFields: Object.keys(data).slice(0, 5),
            remainingFields: Object.keys(data).length > 5 ? Object.keys(data).slice(5) : [],
            totalFieldCount: Object.keys(data).length,
            hasNestedData: !!data.data,
            nestedDataFields: data.data ? Object.keys(data.data) : [],
            updatedPageId: event.updated_page_id
          });
          
          // Update the complete story state with direct object format
          dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
          dispatch({ type: StoryActionTypes.SET_STORY, payload: data });
          
          // Also update the story in the collection if it exists there
          dispatch({ type: StoryActionTypes.UPDATE_STORY_IN_LIST, payload: data });
          
          // Also update the read-aloud story if it's the same story
          if (readAloudStory && readAloudStory.id === data.id) {
            console.log('[StoryContext] Updating read-aloud story from page update');
            dispatch({ type: StoryActionTypes.SET_READ_ALOUD_STORY, payload: data });
          }
          
          // We already updated the full story state above which includes all pages
          // Log information about the updated page but don't dispatch a separate update
          if (event.updated_page_id && data.pages) {
            const updatedPage = data.pages.find(p => p.id === event.updated_page_id);
            if (updatedPage) {
              console.log(`[StoryContext] Page update included in story update: ${updatedPage.id}, page ${updatedPage.page_number}`);
              // No separate dispatch needed - we already updated the whole story
            }
          }
        }
        break;
  
      case 'story_update':
        if (data) {
          // NEW ARCHITECTURE: Use direct object format without 'data' wrapper
          // StoryContext now standardizes on direct object format across the application
          
          // Log the format standardization for debugging with comprehensive details
          console.log('[StoryContext] Standardizing to direct object format for story_update:', {
            originalHasId: !!data.id,
            originalHasUserIsOwner: data.user_is_owner !== undefined,
            originalUserIsOwner: data.user_is_owner,
            dataAllFields: Object.keys(data),
            dataFields: Object.keys(data).slice(0, 5),
            remainingFields: Object.keys(data).length > 5 ? Object.keys(data).slice(5) : [],
            totalFieldCount: Object.keys(data).length,
            hasNestedData: !!data.data,
            nestedDataFields: data.data ? Object.keys(data.data) : [],
            storyId: data.id,
            eventDetails: JSON.stringify(event).substring(0, 100)
          });
          
          // Update the state with direct object format
          dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
          dispatch({ type: StoryActionTypes.SET_STORY, payload: data });
          
          // Also update the story in the collection if it exists there
          dispatch({ type: StoryActionTypes.UPDATE_STORY_IN_LIST, payload: data });
          
          // Also update the read-aloud story if it's the same story
          if (readAloudStory && readAloudStory.id === data.id) {
            console.log('[StoryContext] Updating read-aloud story from WebSocket update');
            dispatch({ type: StoryActionTypes.SET_READ_ALOUD_STORY, payload: data });
          }
          
          // Log how many pages with text we have - but DON'T update each individually
          // This was causing multiple redundant re-renders
          const pagesWithText = data.pages?.filter(p => p.text) || [];
          if (pagesWithText.length > 0) {
            console.log(`[StoryContext] Received initial story state with ${pagesWithText.length} pages with text`);
            // We already updated the full story state above, no need for individual page updates
          }
          
          // Handle initial state notification (first load)
          if (event.is_initial_state) {
            console.log('[StoryContext] Received initial story state');
          }
        }
        break;
  
      case 'story_left':
        // Handle when the server informs us we've left a story room
        if (event.story_id) {
          console.log(`[StoryContext] Received story_left notification for story ${event.story_id}`);
          
          // If this is the current active story, we could optionally:
          // 1. Mark it as inactive (but keep the data for caching purposes)
          // 2. Update any UI state that depends on active story status
          
          // If this matches the readAloudStory, we don't need to handle it here
          // since ReadAloudContext has its own handler for storyLeft events
          
          // No need to clear story state - this just confirms we're no longer 
          // receiving real-time updates for this story, but we can keep the data
          // for quick access if the user returns to this story
        }
        break;
        
      default:
        console.warn('[WebSocket] Received unknown message type:', type);
    }
  }, [story, readAloudStory]);

  // Auth state is now handled by AuthContext, which updates authState directly
  // StoryContext no longer needs to manage auth parameters

  // Initialize WebSocket connection and handlers
  useEffect(() => {
    if (wsConnectionRef.current.isConnecting || wsConnectionRef.current.isConnected) {
      return;
    }

    const wsConnection = wsConnectionRef.current;
    let isComponentMounted = true;

    // Setup WebSocket subscriptions
    if (!wsConnection.messageUnsubscribe) {
      wsConnection.messageUnsubscribe = services.websocket.subscribe('message', handleWebSocketMessage);
    }
    
    if (!wsConnection.errorUnsubscribe) {
      wsConnection.errorUnsubscribe = services.websocket.subscribe('error', (error) => {
        if (!isComponentMounted) return;
        dispatch({ 
          type: StoryActionTypes.SET_ERROR, 
          payload: `WebSocket error: ${error.message}` 
        });
      });
    }

    // Connect to WebSocket
    if (!wsConnection.isConnected && !wsConnection.isConnecting && !wsConnection.hasAttemptedConnection) {
      wsConnection.isConnecting = true;
      wsConnection.hasAttemptedConnection = true;
      
      services.websocket.connect()
        .then(() => {
          if (isComponentMounted) {
            wsConnection.isConnecting = false;
            wsConnection.isConnected = true;
          }
        })
        .catch(error => {
          if (isComponentMounted) {
            wsConnection.isConnecting = false;
            console.warn('Failed to connect to WebSocket:', error.message);
          }
        });
    }

    return () => {
      isComponentMounted = false;
    };
  }, [handleWebSocketMessage]);
  
  // Cleanup on unmount
  useEffect(() => {
    // Set up an unload listener to properly disconnect WebSocket when app closes
    const handleUnload = () => {
      console.log('Application closing, cleaning up WebSocket connection');
      services.websocket.disconnect();
    };
    
    // Listen for forced context reset (e.g., from logout)
    const handleForceReset = (event) => {
      console.log('[StoryContext] Received force reset event:', event.detail);
      
      // Reset story state
      dispatch({ type: StoryActionTypes.RESET_STORY });
      
      // Also reset collection of stories
      dispatch({ type: StoryActionTypes.SET_STORIES, payload: [] });
      
      // Reset read-aloud story
      dispatch({ type: StoryActionTypes.SET_READ_ALOUD_STORY, payload: null });
      
      console.log('[StoryContext] All story state has been reset');
    };
    
    // Only add the listeners once
    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('forceResetAllContexts', handleForceReset);
    
    return () => {
      const wsConnection = wsConnectionRef.current;
      
      // Always unsubscribe to prevent memory leaks
      if (wsConnection.messageUnsubscribe) {
        wsConnection.messageUnsubscribe();
      }
      if (wsConnection.errorUnsubscribe) {
        wsConnection.errorUnsubscribe();
      }
      
      // Remove event listeners
      window.removeEventListener('beforeunload', handleUnload);
      document.removeEventListener('forceResetAllContexts', handleForceReset);
      
      // Don't disconnect the WebSocket on normal component unmounts
      // This allows the connection to persist during navigation
    };
  }, []);

  // Join a story room using the centralized storyService
  const joinStory = useCallback(async (storyId, force = false) => {
    if (!storyId) {
      console.error("Cannot join story: No story ID provided");
      return false;
    }

    try {
      dispatch({ type: StoryActionTypes.SET_LOADING, payload: true });
      
      console.log(`[StoryContext] Using storyService to join story ${storyId}`);
      
      // Using the centralized storyService to handle the WebSocket connection and fallbacks
      // Auth parameters are now handled by the communication service via authState
      const storyData = await storyService.getStory(storyId, force);
      
      if (storyData) {
        // Update the context state with the retrieved story
        dispatch({ type: StoryActionTypes.SET_STORY, payload: storyData });
        dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
        return true;
      } else {
        // No story data returned
        dispatch({ 
          type: StoryActionTypes.SET_ERROR, 
          payload: 'Failed to load story data' 
        });
        dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
        return false;
      }
    } catch (error) {
      console.error(`[StoryService] Error joining story ${storyId}:`, error);
      dispatch({ 
        type: StoryActionTypes.SET_ERROR, 
        payload: 'Failed to join story. Please try again.' 
      });
      dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
      return false;
    }
  }, [isCreatingStory]);

  // Generate a new page using the centralized storyService
  const generatePage = useCallback(async (storyId) => {
    dispatch({ type: StoryActionTypes.SET_LOADING, payload: true });
    dispatch({ type: StoryActionTypes.SET_ERROR, payload: null });
    
    try {
      // Use the storyService to generate a page
      await storyService.generatePage(storyId);
      
      // Set loading to false immediately - the 1 second delay was unnecessarily slowing the UI
      dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
    } catch (error) {
      console.error(`Error generating page:`, error);
      dispatch({ 
        type: StoryActionTypes.SET_ERROR, 
        payload: 'Failed to generate page. Please try again.' 
      });
      dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
    }
  }, []);

  // Start a new story using the centralized storyService
  const startStory = useCallback(async (theme, desiredEnding, characters) => {
    if (!theme || !desiredEnding) {
      throw new Error('Theme and ending are required');
    }
    if (!characters?.length) {
      throw new Error('At least one character is required');
    }
    
    dispatch({ type: StoryActionTypes.SET_LOADING, payload: true });
    dispatch({ type: StoryActionTypes.SET_ERROR, payload: null });
    
    try {
      const storyData = await storyService.createStory({
        title: 'Your Story',
        theme,
        desired_ending: desiredEnding,
        characters: characters.map(char => char.id)
      });
      
      dispatch({ type: StoryActionTypes.SET_STORY, payload: storyData });
      dispatch({ type: StoryActionTypes.SET_CREATING, payload: true });
      
      // Use our centralized joinStory method to join the WebSocket room
      const joinSuccess = await joinStory(storyData.id);
      if (!joinSuccess) {
        console.warn('Failed to join WebSocket story room, but continuing with story creation');
      }
      
      await generatePage(storyData.id);
      return storyData.id;
    } catch (error) {
      dispatch({ 
        type: StoryActionTypes.SET_ERROR, 
        payload: error.message || 'Failed to start story. Please try again.' 
      });
      return null;
    } finally {
      dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
    }
  }, [generatePage, joinStory]);

  // Reset story state
  const resetStory = useCallback(() => {
    dispatch({ type: StoryActionTypes.RESET_STORY });
  }, []);

  // Set story data
  const setStory = useCallback((storyData) => {
    dispatch({ type: StoryActionTypes.SET_STORY, payload: storyData });
  }, []);

  // Set error message
  const setError = useCallback((errorMessage) => {
    dispatch({ type: StoryActionTypes.SET_ERROR, payload: errorMessage });
  }, []);

  // Fetch all user stories using the centralized storyService
  const fetchUserStories = useCallback(async () => {
    dispatch({ type: StoryActionTypes.SET_STORIES_LOADING, payload: true });
    
    try {
      // Use the storyService which now handles auth headers automatically
      const stories = await storyService.getStories();
      dispatch({ type: StoryActionTypes.SET_STORIES, payload: stories || [] });
    } catch (error) {
      console.error('Failed to fetch user stories:', error);
      dispatch({ 
        type: StoryActionTypes.SET_STORIES_ERROR, 
        payload: 'Failed to fetch stories. Please try again.' 
      });
    }
  }, []);
  
  // Auto fetch stories when authentication state changes
  useEffect(() => {
    if (isAuthenticated && activeChildProfile?.id) {
      console.log('[StoryContext] Auth state changed, auto-fetching stories');
      fetchUserStories().catch(err => {
        console.error('[StoryContext] Error auto-fetching stories:', err);
      });
    }
  }, [isAuthenticated, activeChildProfile, fetchUserStories]);
  
  // Leave a story room using the storyService
  const leaveStory = useCallback(async (storyId) => {
    if (!storyId) return;
    
    try {
      // Use WebSocket manager from storyService to leave the room
      await storyService.ws.leaveRoom(storyId, {
        messageType: 'leave_story',
        idField: 'story_id'
      });
      
      return true;
    } catch (error) {
      console.error(`[StoryService] Error leaving story ${storyId}:`, error);
      return false;
    }
  }, []);
  
  // Load a story by ID using storyService
  const loadStory = useCallback(async (storyId) => {
    if (!storyId) {
      dispatch({ type: StoryActionTypes.SET_ERROR, payload: 'No story ID provided' });
      return null;
    }
    
    dispatch({ type: StoryActionTypes.SET_LOADING, payload: true });
    dispatch({ type: StoryActionTypes.SET_ERROR, payload: null });
    
    try {
      // Use our joinStory method which now uses storyService
      const joinSuccess = await joinStory(storyId);
      
      if (!joinSuccess) {
        // If join failed but we still have story data from the API request in joinStory,
        // we can proceed with that data
        if (!story) {
          dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
          return null;
        }
      }
      
      // Return the story data
      return story;
    } catch (error) {
      console.error(`Error loading story:`, error);
      dispatch({ 
        type: StoryActionTypes.SET_ERROR, 
        payload: error.message || 'Failed to load story. Please try again.' 
      });
      dispatch({ type: StoryActionTypes.SET_LOADING, payload: false });
      return null;
    }
  }, [story, joinStory]);
  
  /**
   * Load a story specifically for read-aloud functionality
   * This doesn't change the active story, but makes the story data
   * available for the ReadAloudContext to use
   * 
   * @param {string|number} storyId - The ID of the story to load
   * @returns {Promise<object>} - The loaded story data or null on error
   */
  const loadStoryForReadAloud = useCallback(async (storyId) => {
    if (!storyId) {
      dispatch({ 
        type: StoryActionTypes.SET_READ_ALOUD_STORY_ERROR, 
        payload: 'No story ID provided' 
      });
      return null;
    }
    
    dispatch({ type: StoryActionTypes.SET_READ_ALOUD_STORY_LOADING, payload: true });
    
    try {
      // Check if the requested story is already the active story
      if (story && story.id === parseInt(storyId, 10)) {
        console.log(`[StoryContext] Using existing active story ${storyId} for read-aloud`);
        
        // Store the story in the readAloudStory state
        dispatch({ 
          type: StoryActionTypes.SET_READ_ALOUD_STORY, 
          payload: story 
        });
        
        // Register with WebSocket for updates via storyService
        storyService.ws.registerRoomUsage(storyId, 'ReadAloudContext');
        
        return story;
      }
      
      // Check if the story is already in our collection
      const existingStory = stories.find(s => s.id === parseInt(storyId, 10));
      if (existingStory) {
        console.log(`[StoryContext] Using existing story ${storyId} from collection for read-aloud`);
        
        // Store the story in the readAloudStory state
        dispatch({ 
          type: StoryActionTypes.SET_READ_ALOUD_STORY, 
          payload: existingStory 
        });
        
        // Register with WebSocket for updates
        storyService.ws.registerRoomUsage(storyId, 'ReadAloudContext');
        
        return existingStory;
      }
      
      // If we get here, we need to fetch the story
      console.log(`[StoryContext] Fetching story ${storyId} for read-aloud using storyService`);
      
      // Use storyService which handles auth automatically via the central authState
      const storyData = await storyService.getStory(storyId);
      
      if (!storyData) {
        console.error('[StoryContext] Story data missing for read-aloud');
        throw new Error('Failed to load story data');
      }
      
      // Store the story data in the readAloudStory state
      dispatch({ 
        type: StoryActionTypes.SET_READ_ALOUD_STORY, 
        payload: storyData 
      });
      
      // Register with WebSocket for updates
      storyService.ws.registerRoomUsage(storyId, 'ReadAloudContext');
      
      return storyData;
    } catch (error) {
      console.error(`Error loading story for read-aloud:`, error);
      dispatch({ 
        type: StoryActionTypes.SET_READ_ALOUD_STORY_ERROR, 
        payload: error.message || 'Failed to load story for read-aloud.' 
      });
      return null;
    }
  }, [story, stories]);

  // Create memoized context value
  const contextValue = useMemo(() => ({
    // Single story state
    story,
    isLoading,
    isCreatingStory,
    error,
    
    // Story collection state
    stories,
    storiesLoading,
    storiesError,
    
    // Read-aloud story state
    readAloudStory,
    readAloudStoryLoading,
    readAloudStoryError,
    
    // Methods
    setStory,
    setError,
    startStory,
    generatePage,
    resetStory,
    joinStory,
    leaveStory,
    loadStory,
    fetchUserStories,
    loadStoryForReadAloud
  }), [
    // Single story state
    story,
    isLoading,
    isCreatingStory,
    error,
    
    // Story collection state
    stories,
    storiesLoading,
    storiesError,
    
    // Read-aloud story state
    readAloudStory,
    readAloudStoryLoading,
    readAloudStoryError,
    
    // Methods
    setStory,
    setError,
    startStory,
    generatePage,
    resetStory,
    joinStory,
    leaveStory,
    loadStory,
    fetchUserStories,
    loadStoryForReadAloud
  ]);

  return (
    <StoryContext.Provider value={contextValue}>
      {children}
    </StoryContext.Provider>
  );
}

// Custom hooks
export function useStory() {
  const context = useContext(StoryContext);
  if (context === undefined) {
    throw new Error('useStory must be used within a StoryProvider');
  }
  return context;
}

export function useStoryData() {
  const { story } = useStory();
  return story;
}

export function useStoryLoading() {
  const { isLoading, isCreatingStory } = useStory();
  return { isLoading, isCreatingStory };
}

export function useStoryError() {
  const { error, setError } = useStory();
  return { error, setError };
}

export function useStoryActions() {
  const { startStory, generatePage, resetStory, joinStory, leaveStory, loadStory } = useStory();
  return { startStory, generatePage, resetStory, joinStory, leaveStory, loadStory };
}

// Hook to get story pages
export function useStoryPages() {
  const { story } = useStory();
  return story?.pages || [];
}

// Hook to get a specific page by index
export function useStoryPage(pageIndex) {
  const pages = useStoryPages();
  return pages[pageIndex] || null;
}

// Hook to get story navigation state
export function useStoryNavigation() {
  const { story } = useStory();
  const pages = story?.pages || [];
  
  return {
    currentPageCount: pages.length,
    totalPages: story?.total_pages || 0,
    isComplete: pages.length >= (story?.total_pages || 0),
    hasPages: pages.length > 0
  };
}

// Hook to access the stories collection
export function useStoriesCollection() {
  const { stories, storiesLoading, storiesError, fetchUserStories } = useStory();
  return { stories, isLoading: storiesLoading, error: storiesError, fetchUserStories };
}

// Hook to access the read-aloud story
export function useReadAloudStory() {
  const { 
    readAloudStory, 
    readAloudStoryLoading, 
    readAloudStoryError, 
    loadStoryForReadAloud 
  } = useStory();
  
  return { 
    story: readAloudStory,
    isLoading: readAloudStoryLoading, 
    error: readAloudStoryError, 
    loadStory: loadStoryForReadAloud 
  };
}
